import { useCallback, useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
} from 'reactflow';
import dagre from 'dagre';
import WorkflowStepNode from './WorkflowStepNode';
import { STEP_STATUS_CONFIG } from '../../constants/workflows';
import 'reactflow/dist/style.css';

const nodeTypes = {
  workflowStep: WorkflowStepNode,
};

const NODE_WIDTH = 220;
const NODE_HEIGHT = 80;

/**
 * Auto-layout DAG using dagre
 */
const getLayoutedElements = (steps, stepStates = []) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: 'TB', nodesep: 60, ranksep: 80 });

  // Add nodes
  steps.forEach(step => {
    dagreGraph.setNode(step.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  // Add edges from dependsOn
  steps.forEach(step => {
    (step.dependsOn || []).forEach(dep => {
      dagreGraph.setEdge(dep, step.id);
    });
  });

  dagre.layout(dagreGraph);

  // Build step state map
  const stateMap = {};
  stepStates.forEach(ss => {
    stateMap[ss.stepId] = ss;
  });

  // Create React Flow nodes
  const nodes = steps.map(step => {
    const pos = dagreGraph.node(step.id);
    const state = stateMap[step.id];

    return {
      id: step.id,
      type: 'workflowStep',
      position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 },
      data: {
        step,
        state,
        status: state?.status || 'PENDING',
      },
    };
  });

  // Create edges with status-based styling
  const edges = steps.flatMap(step =>
    (step.dependsOn || []).map(dep => {
      const sourceState = stateMap[dep];
      let strokeColor = '#94a3b8'; // gray-600 default

      if (sourceState?.status === 'COMPLETED') strokeColor = '#22c55e'; // green-500
      else if (sourceState?.status === 'FAILED') strokeColor = '#ef4444'; // red-500
      else if (sourceState?.status === 'RUNNING') strokeColor = '#3b82f6'; // blue-500

      return {
        id: `${dep}-${step.id}`,
        source: dep,
        target: step.id,
        animated: sourceState?.status === 'RUNNING',
        style: { stroke: strokeColor, strokeWidth: 2 },
      };
    })
  );

  return { nodes, edges };
};

const WorkflowCanvas = ({ template, run, onStepClick, className = '' }) => {
  const { nodes, edges } = useMemo(
    () => getLayoutedElements(template?.steps || [], run?.stepStates),
    [template?.steps, run?.stepStates]
  );

  const handleNodeClick = useCallback((event, node) => {
    onStepClick?.(node.data.step, node.data.state);
  }, [onStepClick]);

  const getNodeColor = useCallback((node) => {
    const config = STEP_STATUS_CONFIG[node.data.status] || STEP_STATUS_CONFIG.PENDING;
    // Extract color from border class
    const colorMap = {
      'border-gray-600': '#94a3b8',
      'border-blue-500': '#3b82f6',
      'border-green-500': '#22c55e',
      'border-red-500': '#ef4444',
      'border-yellow-500': '#eab308',
    };
    return colorMap[config.borderColor] || '#94a3b8';
  }, []);

  if (!template?.steps?.length) {
    return (
      <div className={`bg-gray-950 border border-gray-800 flex items-center justify-center ${className}`}>
        <span className="text-gray-500 font-mono text-sm">No steps defined</span>
      </div>
    );
  }

  return (
    <div className={`bg-gray-950 border border-gray-800 ${className}`}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={handleNodeClick}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        attributionPosition="bottom-left"
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#e2e8f0" gap={16} />
        <Controls className="!bg-gray-800 !border-gray-700" />
        <MiniMap
          className="!bg-gray-900"
          nodeColor={getNodeColor}
        />
      </ReactFlow>
    </div>
  );
};

export default WorkflowCanvas;
